import {
  PatientOrgMappingDTO,
  GetTokenResponseDTO,
} from '@/dtos';
import {
  setAuthHeaders,
} from './httpClient';
export type AuthUser = {
  id: string;
  phoneNumber: string;
  name?: string;
  email?: string;
};

export type AuthState = {
  user: PatientOrgMappingDTO | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
};

export const logout = async (): Promise<void> => {
  // Clear all local storage items that might contain auth data
  if (typeof window !== 'undefined') {
    setAuthHeaders();
    clearStoredAuth();
  }
};

// Local storage helpers
export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
};

export const setAuthToken = (token: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('auth_token', token);
};

export const getPatientOrgMappings = (): PatientOrgMappingDTO | null => {
  if (typeof window === 'undefined') return null;
  const mappingsStr = localStorage.getItem('patientOrgMappings');
  return mappingsStr ? JSON.parse(mappingsStr) : null;
};

export const setPatientOrgMapping = (data: PatientOrgMappingDTO) => {
  localStorage.setItem('patientOrgMappings', JSON.stringify(data));
};

export const setStoredAuth = (token: string): void => {
  setAuthToken(token);
};

export const clearStoredAuth = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('patientOrgMappings');
    localStorage.removeItem('signalr_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('session_hash');
  }
};

// Refresh token helpers
export const getRefreshToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('refresh_token');
};

export const setRefreshToken = (refreshToken: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('refresh_token', refreshToken);
};

export const removeRefreshToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('refresh_token');
  }
};

// SignalR token helpers
export const getSignalRToken = (): string | null => {
  return typeof window === 'undefined' ? null : localStorage.getItem('signalr_token');
};

export const setSignalRToken = (token: string): void => {
  if (typeof window !== 'undefined'){
    localStorage.setItem('signalr_token', token);}
};

export const removeSignalRToken = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('signalr_token');
  }
};

// Session hash helpers
export const getSessionHash = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('session_hash');
};

export const setSessionHash = (sessionHash: string): void => {
  if (typeof window === 'undefined') return;
  localStorage.setItem('session_hash', sessionHash);
};

export const removeSessionHash = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('session_hash');
  }
};

// Token validation helper
export const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;
  // In a real app, you might want to decode and validate the JWT token
  // For now, we'll just check if it exists and is not empty
  return token.length > 0;
};

// Auth function to handle token responses that include refresh tokens and session hash
export const handleTokenResponse = (tokenData: GetTokenResponseDTO) => {
  const { token, refreshToken, sessionHash } = tokenData ?? {};
  if (token) {
    setStoredAuth(token);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    if (sessionHash) {
      setSessionHash(sessionHash);
    }
    return { success: true, token };
  }
  return { success: false, error: 'Invalid token response' };
};

export const canViewVisitNotes = (user: PatientOrgMappingDTO | null | undefined): boolean => {
  return user?.patientPortalConfig?.shouldShowVisitNotes ?? false;
};
